import { useMutation, useSuspenseQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { FacilityWasteView } from "generated-graphql/graphql";

import { useWasteFacilityState } from "../WasteFacilityContext";

export const GET_WASTE_STREAM = gql(`
  query GetWasteStream(
    $id: ID!
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
  ) {
    wasteStream(id: $id, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags) {
      id
      name
      facilityId
      classification
      wasteKind
      overriddenWasteKind
      universalWasteCategory
      federalWasteCodes
      stateWasteCodes
      eManifestProfileNumber
      vendorProfileNumber
      managementMethodCodes
      formCode
      sourceCode
      wasteMinimizationCode
      vendorFormCode
      eManifestFormCode
      vendorSourceCode
      eManifestSourceCode
      vendorAmount
      eManifestAmount
      documentAmount
      effectiveAmount
      vendorDotDescription
      eManifestDotDescription
      dotHazardous
      vendorManifestCount
      eManifestCount
      vendorManifestLineItemCount
      eManifestLineItemCount
      lastShipmentDate
      lastShipmentDateAllTime
      internalNote
      comment
      willNotReport
      wasteKind
      overriddenWasteKind
      countsTowardGeneration
      mergeCount
      groupingProfile {
        source
        value
      }
      needsSubmission
      registrationDisplayStatus
      registration {
        id
        stateRegisteredId
        name
        status
        registeredAt
      }
      facility {
        id
        name
        state
      }
      isProvisional
      discrepancyCount
      discrepancies {
        id
        field
        wasteLineUnifiedId
        processedManifestId
        kind
        status
        eManifestValue
        vendorValue
        manifestDocumentValue
      }
    }
  }
`);

export const useWasteStream = (id: string) => {
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { startDate, endDate, view, tags } = deferredFilters;
  // Dates are optional so the modal honors a single-ended filter; the backend
  // omits whichever bound is missing.
  if (!view) {
    throw new Error("view is required");
  }
  return useWasteStreamWithParams(id, startDate, endDate, view, tags);
};

export const useWasteStreamWithParams = (
  id: string,
  startDate: string | undefined,
  endDate: string | undefined,
  view: FacilityWasteView,
  tags: string[] | undefined
) => {
  return useSuspenseQuery(GET_WASTE_STREAM, {
    variables: { id, startDate, endDate, view, tags },
  });
};

export const UPDATE_WASTE_STREAM = gql(`
  mutation UpdateWasteStream($input: UpdateWasteStreamInput!) {
    updateWasteStream(input: $input) 
  }
`);

export const useUpdateWasteStream = () => {
  return useMutation(UPDATE_WASTE_STREAM, {
    refetchQueries: [GET_WASTE_STREAM],
  });
};

export const SET_WASTE_KIND = gql(`
  mutation SetWasteKind(
    $wasteStreamId: ID!
    $kind: WasteKind
    $universalWasteCategory: UniversalWasteCategory
  ) {
    setWasteKind(
      wasteStreamId: $wasteStreamId
      kind: $kind
      universalWasteCategory: $universalWasteCategory
    )
  }
`);

export const useSetWasteKind = () => {
  return useMutation(SET_WASTE_KIND, {
    refetchQueries: [GET_WASTE_STREAM],
  });
};
